import { useState, useCallback } from 'react';

const FAQ_ITEMS = [
  {
    q: '榴莲视频需要注册才能观看吗？',
    a: '不需要。榴莲视频所有内容均免费开放，无需注册、无需登录即可在线观看。我们致力于降低用户门槛，打开网站就能直接播放您感兴趣的视频内容。',
  },
  {
    q: '视频播放卡顿怎么办？',
    a: '我们部署了全球分布式 CDN 加速节点，通常情况下播放流畅无缓冲。如果遇到卡顿，建议检查本地网络状态，或尝试切换视频清晰度。APP 端支持智能码率自适应，可根据网速自动调整。',
  },
  {
    q: '平台支持哪些设备观看？',
    a: '榴莲视频全面适配 PC 端、平板电脑及手机浏览器，同时提供 iOS 与 Android 原生 APP。无论使用什么设备，均可获得一致的高清观影体验，并支持 Chromecast 投屏到电视大屏观看。',
  },
  {
    q: '内容更新频率是怎样的？',
    a: '我们拥有专业的内容运营团队，每日更新超过 300 部精选视频资源。涵盖影视、综艺、动漫、短视频等多个分类，确保用户每次访问都能发现新鲜内容。热门榜单与专题合集也会定期更新。',
  },
];

export default function FaqContactSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('idle');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    // honeypot check
    const hp = formData.get('phone_alt');
    if (hp && String(hp).trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d9lpi5obdkpk99j7g3fg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([k]) => k !== 'phone_alt')
            .map(([k, v]) => [k, String(v)])
        ).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (res.ok && parsed && (parsed as any).code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg =
          (parsed as any)?.meta?.message ||
          (parsed as any)?.message ||
          (parsed as any)?.meta?.detail ||
          responseText;
        if (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
          setFormStatus('error');
          setFormError('提交失败，请稍后重试');
        } else {
          setFormStatus('error');
          setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络异常，请检查连接后重试');
    }
  }, []);

  return (
    <>
      {/* #faq */}
      <section id="faq" className="py-16 md:py-24 px-4 md:px-6 bg-background-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-4">
              <i className="ri-question-answer-line text-sm"></i>
              常见问题
            </div>
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">
              快速解答你的疑问
            </h2>
          </div>

          <div className="space-y-3">
            {FAQ_ITEMS.map((item, idx) => (
              <div
                key={idx}
                className="rounded-xl border border-background-200/60 bg-background-50 overflow-hidden transition-all duration-200"
              >
                <button
                  onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-background-100/50 transition-colors duration-150"
                >
                  <h3 className="text-sm md:text-base font-medium text-foreground-950 pr-4">
                    {item.q}
                  </h3>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-transform duration-300 ${openIndex === idx ? 'bg-primary-500 rotate-180' : 'bg-background-200'}`}>
                    <i className={`ri-arrow-down-s-line text-sm ${openIndex === idx ? 'text-background-50' : 'text-foreground-600'}`}></i>
                  </div>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openIndex === idx ? 'max-h-64 pb-4' : 'max-h-0'
                  }`}
                >
                  <p className="px-5 text-sm text-foreground-600 leading-relaxed">
                    {item.a}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* #contact */}
      <section id="contact" className="py-16 md:py-24 px-4 md:px-6 bg-background-100">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl bg-background-50 border border-background-200/60 p-8 md:p-12">
            <div className="flex flex-col md:flex-row items-center gap-8">
              {/* Left Info */}
              <div className="flex-1 w-full text-center md:text-left">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
                  <i className="ri-mail-send-line text-sm"></i>
                  联系我们
                </div>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4">
                  意见反馈 · 商务合作
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  如果您在使用过程中有任何建议或遇到问题，欢迎通过以下方式联系我们。
                  商务合作、内容投稿、广告投放等事宜也可通过邮箱联络，我们将在 24 小时内回复。
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 justify-center md:justify-start">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                      <i className="ri-mail-line text-primary-500"></i>
                    </div>
                    <div className="text-left">
                      <div className="text-xs text-foreground-500">电子邮箱</div>
                      <div className="text-sm font-medium text-foreground-950">contact@liulian-sp.com</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 justify-center md:justify-start">
                    <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center shrink-0">
                      <i className="ri-customer-service-2-line text-accent-500"></i>
                    </div>
                    <div className="text-left">
                      <div className="text-xs text-foreground-500">客服时间</div>
                      <div className="text-sm font-medium text-foreground-950">每天 9:00 - 24:00</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Quick Message */}
              <div className="flex-1 w-full">
                <div className="bg-background-100 rounded-xl p-6">
                  <h3 className="font-medium text-foreground-950 mb-4 text-center">快速留言</h3>
                  <form className="space-y-3" onSubmit={handleSubmit} data-readdy-form="d9lpi5obdkpk99j7g3fg">
                    <input
                      type="text"
                      name="name"
                      placeholder="您的昵称"
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200/60 bg-background-50 text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="您的邮箱"
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200/60 bg-background-50 text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    />
                    <textarea
                      name="message"
                      placeholder="请输入您的留言内容…"
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200/60 bg-background-50 text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    ></textarea>
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="hp-field"
                    />
                    {formStatus === 'success' && (
                      <div className="text-sm text-green-600 bg-green-50 rounded-lg px-3 py-2 text-center">
                        <i className="ri-check-line mr-1"></i>留言已发送，我们会尽快回复！
                      </div>
                    )}
                    {formStatus === 'error' && (
                      <div className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 text-center">
                        <i className="ri-error-warning-line mr-1"></i>{formError}
                      </div>
                    )}
                    <button
                      type="submit"
                      className="w-full py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                    >
                      发送留言
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}